import React from 'react';
import { X, CheckCircle2, AlertCircle, File, Minimize2, Maximize2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export interface UploadProgress {
  id: string;
  fileName: string;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

interface UploadProgressPopupProps {
  uploads: UploadProgress[];
  onClose: () => void;
  onCancel?: (id: string) => void;
}

export const UploadProgressPopup: React.FC<UploadProgressPopupProps> = ({
  uploads,
  onClose,
  onCancel,
}) => {
  const [isMinimized, setIsMinimized] = React.useState(false);

  // Auto-dismiss after all uploads complete (with a small delay)
  React.useEffect(() => {
    const allComplete = uploads.length > 0 && uploads.every(u => u.status === 'success' || u.status === 'error');
    const allSuccess = uploads.length > 0 && uploads.every(u => u.status === 'success');

    if (allComplete && allSuccess) {
      const timer = setTimeout(() => {
        onClose();
      }, 2000); // Auto-dismiss after 2 seconds when all successful
      return () => clearTimeout(timer);
    }
  }, [uploads, onClose]);

  if (uploads.length === 0) return null;

  const completedCount = uploads.filter(u => u.status === 'success').length;
  const failedCount = uploads.filter(u => u.status === 'error').length;
  const uploadingCount = uploads.filter(u => u.status === 'uploading' || u.status === 'pending').length;
  const totalCount = uploads.length;

  const overallProgress = uploads.reduce((sum, upload) => sum + upload.progress, 0) / totalCount;

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 100, scale: 0.95 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="fixed bottom-6 right-6 z-50 w-96 bg-card border border-border rounded-lg shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-muted/50">
          <div className="flex items-center gap-3">
            <div className="relative">
              <svg className="w-8 h-8 transform -rotate-90">
                <circle
                  cx="16"
                  cy="16"
                  r="14"
                  stroke="currentColor"
                  strokeWidth="3"
                  fill="none"
                  className="text-muted"
                />
                <circle
                  cx="16"
                  cy="16"
                  r="14"
                  stroke="currentColor"
                  strokeWidth="3"
                  fill="none"
                  className="text-workspace-accent transition-all duration-300"
                  strokeDasharray={`${2 * Math.PI * 14}`}
                  strokeDashoffset={`${2 * Math.PI * 14 * (1 - overallProgress / 100)}`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-foreground">
                {Math.round(overallProgress)}%
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                {uploadingCount > 0 ? 'Uploading files...' : 'Upload complete'}
              </h3>
              <p className="text-xs text-muted-foreground">
                {completedCount} of {totalCount} files
                {failedCount > 0 && ` (${failedCount} failed)`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
              title={isMinimized ? 'Maximize' : 'Minimize'}
            >
              {isMinimized ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
            </button>
            {uploadingCount === 0 && (
              <button
                onClick={onClose}
                className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                title="Close"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Upload List */}
        <AnimatePresence>
          {!isMinimized && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              exit={{ height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="max-h-80 overflow-y-auto p-4 space-y-3">
                {uploads.map((upload) => (
                  <div
                    key={upload.id}
                    className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg border border-border"
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {upload.status === 'success' ? (
                        <CheckCircle2 size={20} className="text-green-500" />
                      ) : upload.status === 'error' ? (
                        <AlertCircle size={20} className="text-red-500" />
                      ) : (
                        <File size={20} className="text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {upload.fileName}
                      </p>
                      {upload.status === 'error' && upload.error && (
                        <p className="text-xs text-red-500 mt-1">{upload.error}</p>
                      )}
                      {upload.status === 'success' && (
                        <p className="text-xs text-green-500 mt-1">Upload complete</p>
                      )}
                      {(upload.status === 'uploading' || upload.status === 'pending') && (
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                            <span>{upload.status === 'pending' ? 'Pending...' : 'Uploading...'}</span>
                            <span>{Math.round(upload.progress)}%</span>
                          </div>
                          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                            <motion.div
                              className="h-full bg-workspace-accent rounded-full"
                              initial={{ width: 0 }}
                              animate={{ width: `${upload.progress}%` }}
                              transition={{ duration: 0.3 }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    {(upload.status === 'uploading' || upload.status === 'pending') && onCancel && (
                      <button
                        onClick={() => onCancel(upload.id)}
                        className="flex-shrink-0 p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                        title="Cancel upload"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};
